/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RÉSOUDRE UN LIEU — depuis une saisie libre, ou depuis un NIN.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ PUR, ET DANS UN `.ts` — PAS DANS L'ÉCRAN. Deux défauts coûteux du
 * 24/09/2026 vivaient dans `champs-guichet.tsx`, et aucun essai de ce dépôt ne
 * pouvait les voir : rien n'y monte un composant React. La règle du dépôt est
 * déjà écrite en creux (`signatures-plan.ts`, `styles-plan.ts` sont des `.ts`
 * purs justement pour être éprouvés) — on la suit.
 */

/** Casse et accents écartés : le référentiel écrit « Boké », l'agent tape « BOKE ». */
export const aplatir = (v: string): string => v
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLocaleLowerCase('fr-FR').trim();

/**
 * Ce qu'une saisie libre DÉSIGNE dans une liste — ou rien.
 *
 * ⚠️⚠️ SON ABSENCE A BLOQUÉ LE GUICHET EN PRODUCTION. Mesuré le 24/09/2026 sur
 * la capture du fondateur : « Conakry » s'affiche dans Région, et Préfecture
 * répond toujours « Choisissez d'abord la région ». Taper invalide le choix —
 * ce qui est juste, un texte partiel ne désigne rien — et RIEN ne le
 * recommettait à la sortie du champ. Le champ restait affiché PLEIN et vide en
 * valeur, et toute la cascade en aval attendait un parent qui n'arrivait
 * jamais.
 *
 * ⚠️ ET LE `required` DU NAVIGATEUR NE POUVAIT PAS LE VOIR : il porte sur
 * l'`<input>`, dont la valeur est le TEXTE AFFICHÉ. Le formulaire aurait été
 * déposé sans région, validation passée.
 *
 * ⚠️ DEUX HOMONYMES NE SE TRANCHENT PAS TOUT SEULS. « Lambanyi » existe dans
 * deux préfectures ; en choisir un au hasard poserait un lieu faux que
 * personne n'aurait décidé. On rend `null`, et l'agent choisit dans la liste.
 */
export function resoudreSaisie<T extends { libelle: string }>(
  saisie: string, source: readonly T[],
): T | null {
  const q = aplatir(saisie);
  if (!q) return null;
  const exactes = source.filter((o) => aplatir(o.libelle) === q);
  return exactes.length === 1 ? exactes[0]! : null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CE QUE LE CINQUIÈME GROUPE DU NIN DÉSIGNE — et ce qu'il ne désigne pas.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fondateur, 24/09/2026 : « le lieu de naissance doit être cohérent avec le
 * NIN, par exemple on sait que le code 003 correspond à Matoto ».
 *
 * ⚠️ VÉRIFIÉ EN BASE, PAS CRU SUR PAROLE : `ter.circonscription` porte
 * `code = '003'` → **Matoto**, échelon préfecture. Les 38 préfectures du pays
 * (33, plus les 5 communes de Conakry que ce référentiel compte comme
 * préfectures) y portent un code de trois chiffres, et ces 38 codes sont
 * DISTINCTS.
 *
 * ⚠️⚠️ LE MÊME CODE EXISTE À DEUX ÉCHELONS. `003` est aussi le code de la
 * COMMUNE de Matoto, et 36 autres codes sont dans ce cas : chercher sans
 * borner l'échelon rend deux lignes, et prendre la première ferait dépendre le
 * lieu inscrit de l'ordre de la base. On borne à `prefecture`, où le code est
 * seul.
 *
 * ⚠️⚠️ ET CETTE CORRESPONDANCE N'EST ÉTABLIE QUE PAR UN CAS. Un seul couple a
 * été confronté au réel (003 = Matoto) ; la structure concorde — trois
 * chiffres, 38 entités, codes uniques — mais une structure n'est pas une
 * preuve. C'est pourquoi un désaccord AVERTIT et ne REFUSE pas : refuser sur
 * une table non établie renverrait chez lui un usager dont le numéro est bon.
 *
 * ⚠️ UN NÉ À L'ÉTRANGER N'A PAS DE PRÉFECTURE. Quand le premier chiffre du NIN
 * vaut 3, le cinquième groupe ne désigne pas une circonscription guinéenne :
 * lui en attribuer une inscrirait un lieu de naissance FAUX sur un acte d'état
 * civil. On ne résout pas, et on dit pourquoi.
 */
export type LectureLieu = { nom: string } | { raison: string } | null;

export function lieuDuNin(
  code: string | null | undefined,
  neALEtranger: boolean,
  /**
   * ⚠️⚠️ `null` VEUT DIRE « PAS ENCORE CHARGÉ », ET CE N'EST PAS `[]`.
   * Défaut trouvé dans mon propre lot avant déploiement : le crochet aplatissait
   * les deux sur un tableau vide, et le verdict affichait alors « Le code 003 ne
   * correspond à aucune préfecture du référentiel » — une PANNE de chargement
   * présentée comme un FAIT d'état civil, sur un numéro parfaitement juste.
   * C'est la forme exacte que ce dépôt traque : deux états opposés qui
   * s'affichent pareil.
   */
  prefectures: ReadonlyArray<{ code: string | null; nom: string }> | null,
): LectureLieu {
  if (!code) return null;
  if (prefectures === null) {
    return { raison: "Le référentiel territorial n'est pas chargé : le groupe « lieu » "
      + "n'a pas pu être résolu. Ce n'est pas un défaut du numéro." };
  }
  if (neALEtranger) {
    return { raison: "Né(e) hors de Guinée : le groupe « lieu » ne désigne pas une préfecture du pays." };
  }
  const trouves = prefectures.filter((p) => p.code === code);
  if (trouves.length === 1) return { nom: trouves[0]!.nom };
  return {
    raison: trouves.length === 0
      ? `Le code « ${code} » ne correspond à aucune préfecture du référentiel.`
      : `Le code « ${code} » désigne ${trouves.length} préfectures : il ne tranche pas.`,
  };
}
